import React, { useEffect, useRef, useState } from "react";
import CustomButton from "src/components/CustomButton";
import {
  Box,
  Divider,
  Grid,
  TextField,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import backButton from "../../Images/backButton.svg";
import { ADD_BANNER, UPDATE_BANNER } from "src/graphql/mutations";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { uploadImage } from "src/components/Common/Utils";
import RichTextEditor from "src/components/Common/TextEditor";
import { bannerPageProps } from "./bannerDetails";
import moment from "moment";
import { videoExtensions } from "src/constant";
import DisplayVideo from "src/components/video/DisplayVideo";
import Select from "react-select";

const BannerModal: React.FC<bannerPageProps> = ({
  refetchBanner,
  handleClose,
  initialBannerObj = {
    title: "",
    imageUrl: "",
    redirectUrl: "",
    description: "",
    status: "",
    type: "",
    section: "",
    verifyBy: "",
    startDate: "",
    endDate: "",
  },
  bannerObj = {
    _id: "",
    title: "",
    imageUrl: "",
    redirectUrl: "",
    description: "",
    status: "",
    type: "",
    section: "",
    verifyBy: "",
    startDate: "",
    endDate: "",
  },
  setBannerObj = () => { },
  isEditBanner = false,
}) => {
  const [redirectUrl, setRedirectUrl] = useState<string | null | undefined>("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [createBanner] = useMutation(ADD_BANNER);
  const [updateBanner] = useMutation(UPDATE_BANNER);
  const imageRef = useRef<HTMLInputElement | null>(null);
  const [desktopUrl, setDesktopUrl] = useState<string>("");

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files?.[0];
      const fileUrl = URL.createObjectURL(file);

      setBannerObj((prev: any) => ({
        ...prev,
        [type]: file,
      }));
    }
  };

  const isVideoUrl = (url: string | File): boolean => {
    if (typeof url === "string") {
      return videoExtensions.some(ext => url.endsWith(ext));
    } else if (url instanceof File) {
      return url.type.startsWith("video/");
    }
    return false;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const missingFields: { field: string; message: string }[] = [];

    if (!bannerObj?.title)
      missingFields.push({ field: "title", message: "Title is required" });
    if (!bannerObj?.section)
      missingFields.push({ field: "section", message: "Section is required" });
    if (!bannerObj?.imageUrl)
      missingFields.push({
        field: "imageUrl",
        message: "Banner Image / Video is required",
      });
    if (missingFields.length > 0) {
      missingFields.forEach((item, index) => {
        setTimeout(() => {
          toast.error(item.message);
        }); // Staggering the toasts by 300ms
      });
      return;
    }
    try {
      if (
        bannerObj?.imageUrl &&
        typeof bannerObj.imageUrl !== "string" &&
        bannerObj.imageUrl instanceof File
      ) {
        const response = await uploadImage(
          bannerObj.imageUrl,
          "Banner",
          bannerObj.title
        );
        if (response?.uploadImage?.url) {
          bannerObj.imageUrl = response.uploadImage.url;
        }
      }

      // Update the Banner object with the new Banner URL
      const updatedBannerObj = {
        ...bannerObj,
      };
      // Determine whether to create a new Banner or update an existing one
      const { data } = isEditBanner
        ? await updateBanner({
          variables: {
            _id: updatedBannerObj?._id,
            input: {
              title: updatedBannerObj?.title,
              imageUrl: updatedBannerObj?.imageUrl,
              description: updatedBannerObj?.description,
              section: updatedBannerObj?.section,
              type: updatedBannerObj?.type,
              redirectUrl: updatedBannerObj?.redirectUrl,
              startDate: updatedBannerObj?.startDate,
              endDate: updatedBannerObj?.endDate,
            },
          },
        })
        : await createBanner({
          variables: {
            input: {
              title: updatedBannerObj?.title,
              imageUrl: updatedBannerObj?.imageUrl,
              description: updatedBannerObj?.description,
              section: updatedBannerObj?.section,
              type: updatedBannerObj?.type,
              redirectUrl: updatedBannerObj?.redirectUrl,
              startDate: updatedBannerObj?.startDate,
              endDate: updatedBannerObj?.endDate,
            },
          },
        });

      // Refetch Banner to update the list
      refetchBanner();

      // Provide feedback to the user
      if (
        data?.createBanner?.statusCode === 200 ||
        data?.updateBanner?.statusCode === 200
      ) {
        toast.success(
          data?.createBanner?.message ||
          data?.updateBanner?.message ||
          "Banner saved successfully"
        );
        setBannerObj(initialBannerObj);
        handleClose();
      } else {
        toast.error(data?.createBanner?.message || data?.updateBanner?.message || "Error saving Banner");
      }
    } catch (error: any) {
      toast.error(error?.message || "An unexpected error occurred.");
    }
  };

  useEffect(() => {
    if (isEditBanner && bannerObj) {
      // setBannerObj(bannerObj);
      setBannerObj({
        ...bannerObj,
        startDate: moment(bannerObj.startDate).format("YYYY-MM-DD"),
        endDate: moment(bannerObj.endDate).format("YYYY-MM-DD"),
      });
    }
  }, [isEditBanner, bannerObj]);

  const urlRegex = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/;

  useEffect(() => {
    const url = bannerObj?.redirectUrl ?? "";

    if (url.trim() !== "") {
      if (urlRegex.test(url)) {
        setRedirectUrl(url);
        setUrlError(null);
      } else {
        setRedirectUrl("");
        setUrlError(
          "Please enter a valid URL (starting with http:// or https://)"
        );
      }
    } else {
      setRedirectUrl("");
      setUrlError(null);
    }
  }, [bannerObj?.redirectUrl]);

  return (
    <Box p={2}>
      <Box style={{ display: "flex", alignItems: "center" }}>
        <Tooltip title="Back">
          <img
            src={backButton}
            alt="backButton"
            height={25}
            width={25}
            style={{ marginRight: "12px", cursor: "pointer" }}
            onClick={handleClose}
          />
        </Tooltip>
        <Typography
          variant="h4"
          gutterBottom
          style={{ color: "#00C5B9" }}
          marginTop={2}
        >
          {isEditBanner ? "Update Banner" : "Create Banner"}
        </Typography>
      </Box>

      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="title"
              margin="dense"
              label="Title"
              type="text"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={bannerObj?.title || ""}
              onChange={(e) =>
                setBannerObj({ ...bannerObj, title: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="type"
              margin="dense"
              label="Enter Type"
              type="text"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={bannerObj?.type || ""}
              onChange={(e) =>
                setBannerObj({
                  ...bannerObj,
                  type: e.target.value.toUpperCase(),
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Select
              value={
                bannerObj?.section
                  ? { value: bannerObj?.section, label: bannerObj?.section }
                  : null
              }
              placeholder="Select Sections"
              onChange={(selectedOption) => {
                setBannerObj((prev: any) => ({
                  ...prev,
                  section: selectedOption?.value || "",
                }));
              }}
              options={[
                { value: "TOP", label: "Top" },
                { value: "MIDDLE", label: "Middele" },
                { value: "BOTTOM", label: "Bottom" },
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="redirectUrl"
              margin="dense"
              label="Enter Redirect URL"
              type="text"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={bannerObj?.redirectUrl || ""}
              onChange={(e) =>
                setBannerObj({ ...bannerObj, redirectUrl: e.target.value })
              }
              error={!!urlError}
              helperText={urlError}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="startDate"
              margin="dense"
              label="Enter Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={bannerObj?.startDate || ""}
              InputProps={{
                inputProps: {
                  min: moment(new Date()).format("YYYY-MM-DD"),
                },
              }}
              onChange={(e) =>
                setBannerObj({
                  ...bannerObj,
                  startDate: e.target.value, // Storing date as a string (YYYY-MM-DD format)
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="endDate"
              margin="dense"
              label="Enter End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={bannerObj?.endDate || ""}
              InputProps={{
                inputProps: {
                  min: moment(new Date()).format("YYYY-MM-DD"),
                },
              }}
              onChange={(e) =>
                setBannerObj({
                  ...bannerObj,
                  endDate: e.target.value, // Storing date as a string (YYYY-MM-DD format)
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">
              <strong style={{ color: "#333" }}>Description </strong>
            </Typography>
            <RichTextEditor
              value={bannerObj?.description || ""}
              onChange={(value) =>
                setBannerObj({ ...bannerObj, description: value })
              }
              placeholder="Enter Banner description here..."
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Banner Image / Video
            </Typography>
            {bannerObj?.imageUrl ? (
              <Box
                position="relative"
                width="100%"
                height="auto"
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Box>
                  {isVideoUrl(bannerObj?.imageUrl) ? (
                    <DisplayVideo
                      item={
                        typeof bannerObj?.imageUrl === "string"
                          ? bannerObj?.imageUrl
                          : URL.createObjectURL(bannerObj?.imageUrl)
                      }
                    />
                  ) : (
                    <img
                      src={
                        typeof bannerObj?.imageUrl === "string"
                          ? bannerObj?.imageUrl
                          : URL.createObjectURL(bannerObj?.imageUrl)
                      }
                      alt="Banner Upload"
                      style={{
                        width: "100%",
                        height: "auto",
                        maxHeight: "500px",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Box>
                <CustomButton
                  variant="outlined"
                  color="warning"
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    zIndex: 1,
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    minWidth: "0",
                    backgroundColor: "white",
                  }}
                  onClick={() => {
                    setBannerObj((prev: any) => ({
                      ...prev,
                      imageUrl: "",
                    }));
                  }}
                  aria-label="Remove image"
                >
                  <Tooltip title="Remove">
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",

                        borderRadius: "50%",
                      }}
                    >
                      &times;
                    </Box>
                  </Tooltip>
                </CustomButton>
              </Box>
            ) : (
              <label>
                <input
                  ref={imageRef}
                  type="file"
                  accept="image/*,  video/mp4, video/webm, video/ogg, video/quicktime, video/x-matroska"
                  style={{ display: "none" }}
                  onChange={(e) => handleFileChange(e, "imageUrl")}
                />
                <CustomButton
                  variant="outlined"
                  size="small"
                  onClick={() => imageRef.current?.click()}
                >
                  Upload Banner Image / Video
                </CustomButton>
                <TextField
                  label="Or Enter Image URL"
                  value={desktopUrl ?? (bannerObj?.imageUrl || "")}
                  onBlurCapture={(e) => {
                    if (desktopUrl?.startsWith("https")) {
                      setBannerObj((prev: any) => ({
                        ...prev,
                        imageUrl: desktopUrl,
                      }));
                    } else {
                      setDesktopUrl("");
                      setBannerObj((prev: any) => ({
                        ...prev,
                        imageUrl: "",
                      }));
                    }
                  }}
                  onChange={(e) => setDesktopUrl(e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </label>
            )}
          </Grid>
          <Box marginTop={2} width={"100%"}>
            <CustomButton onClick={handleSubmit}>{isEditBanner ? "Update Banner" : "Save Banner"}</CustomButton>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

export default BannerModal;
